// Scene views (presentation-2 contract section 7.4). Pure: expands a scene's tabs for a table,
// says which tab draws each anchor, counts lit targets per tab for the badges, and picks the tab
// auto-focus should show. Hidden tabs still count: a target inside one is a table target.

import type { TableModel } from '../model/table.ts';
import type { BrickInstance, Views } from '../presentation/types.ts';
import { parseSelector, selectZones } from '../presentation/selectors.ts';
import { seatsFor } from './repeat.ts';

export interface Tab {
  /** Unique per table: the tab ID, plus `:<seat>` for a perSeat tab. */
  key: string;
  id: string;
  label: string;
  hotkey: string | null;
  badge: string | null;
  icon?: string;
  description?: string;
  seat: string | null;
  /** Shown only while the step lights a target in it. */
  onlyWithTargets: boolean;
  /** Navigation-only zone existence gate; optional for established manually built tabs. */
  onlyWithZones?: boolean;
  /** Mirrors targets other tabs also light: counted in its badge, ignored by auto-focus. */
  passive?: boolean;
  background?: string;
  groupLabel?: string;
  bricks: BrickInstance[];
}

type Table = Pick<TableModel, 'zones' | 'seats' | 'selfSeat' | 'objects' | 'counters'>;

/** One tab per entry, or one per opponent for `perSeat` tabs (their key counts up from the tab's key). */
export function expandTabs(views: Views, table: Pick<TableModel, 'seats'>): Tab[] {
  const out: Tab[] = [];
  for (const t of views.tabs) {
    if (t.perSeat === 'opponents') {
      table.seats.filter(s => !s.isSelf).forEach((s, i) => out.push({
        key: `${t.id}:${s.id}`, id: t.id, label: t.label.replaceAll('{seat}', s.label), hotkey: t.groupLabel ? t.key ?? null : bump(t.key, i),
        ...(t.groupLabel ? { groupLabel: t.groupLabel } : {}),
        badge: t.badge ?? null, ...(t.icon ? { icon: t.icon } : {}), ...(t.description ? { description: t.description } : {}), seat: s.id, onlyWithTargets: t.showWhen === 'targets', ...(t.showWhen === 'zones' ? { onlyWithZones: true } : {}), ...(t.background ? { background: t.background } : {}), ...(t.passive ? { passive: true } : {}), bricks: t.bricks,
      }));
    } else out.push({ key: t.id, id: t.id, label: t.label, hotkey: t.key ?? null, badge: t.badge ?? null, ...(t.icon ? { icon: t.icon } : {}), ...(t.description ? { description: t.description } : {}), seat: null, onlyWithTargets: t.showWhen === 'targets', ...(t.showWhen === 'zones' ? { onlyWithZones: true } : {}), ...(t.background ? { background: t.background } : {}), ...(t.passive ? { passive: true } : {}), bricks: t.bricks });
  }
  return out;
}

/** Navigation is derived from leaves, never used for placement or legality. */
export interface NavigationEntry { key: string; label: string; hotkey: string | null; badge: string | null; icon?: string; description?: string; children: Tab[]; leafKey: string }
export function navigationEntries(tabs: Tab[], current: string | null, remembered: ReadonlyMap<string, string> = new Map()): NavigationEntry[] {
  const out: NavigationEntry[] = [];
  for (const tab of tabs) {
    if (!tab.groupLabel) { out.push({ key: tab.key, label: tab.label, hotkey: tab.hotkey, badge: tab.badge, ...(tab.icon ? { icon: tab.icon } : {}), ...(tab.description ? { description: tab.description } : {}), children: [tab], leafKey: tab.key }); continue; }
    if (out.some(e => e.key === tab.id)) continue;
    const children = tabs.filter(t => t.id === tab.id && t.groupLabel);
    const leafKey = children.find(t => t.key === current)?.key ?? children.find(t => t.key === remembered.get(tab.id))?.key ?? children[0]!.key;
    out.push({ key: tab.id, label: tab.groupLabel, hotkey: tab.hotkey, badge: tab.badge, ...(tab.icon ? { icon: tab.icon } : {}), ...(tab.description ? { description: tab.description } : {}), children, leafKey });
  }
  return out;
}

/** Distinct visible objects, including overlapping selectors only once. */
export function navigationObjectCount(entry: NavigationEntry, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'zones'>): number {
  const zones = new Set(entry.children.flatMap(t => [...(zonesByTab.get(t.key) ?? [])]));
  return new Set(table.zones.filter(z => zones.has(z.id)).flatMap(z => z.objects.filter(o => !o.stack).map(o => o.id))).size;
}

function bump(key: string | undefined, n: number): string | null {
  if (!key) return null;
  const code = key.charCodeAt(0) + n;
  const next = String.fromCharCode(code);
  return /^[1-9a-z]$/.test(next) && next !== 'm' ? next : null;
}

/** Zones each tab's bricks draw (their `select`, nested `slots` included), by tab key. */
export function tabZones(tabs: Tab[], table: Table): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const tab of tabs) {
    const zones = new Set<string>();
    const visit = (b: BrickInstance, inheritedSeat: string | null = tab.seat) => {
      if (b.brick === 'repeat') {
        for (const s of seatsFor(b, table.seats, table)) if (b.child) visit(b.child, s.id);
        return;
      }
      const seat = b.seat === '{self}' ? table.selfSeat : b.seat && b.seat !== '{seat}' ? b.seat : inheritedSeat;
      if (b.select) {
        const sel = parseSelector(b.select);
        if (typeof sel !== 'string') for (const z of selectZones(sel, table.zones, seat, table.selfSeat, new Set())) zones.add(z.id);
      }
      // Seat targets belong to the tab that actually draws their mat/strip.
      if (b.brick === 'opponent-strip' || b.brick === 'seat-mat') {
        const owner = seat ?? table.selfSeat;
        if (owner && table.seats.some(s => s.id === owner)) zones.add(`mat:${owner}`);
      }
      if (typeof b.props?.objectsFrom === 'string') {
        const sel = parseSelector(b.props.objectsFrom);
        if (typeof sel !== 'string') for (const z of selectZones(sel, table.zones, seat, table.selfSeat, new Set())) zones.add(z.id);
      }
      // Tiles are anchors of their own; the tab that draws them holds their targets.
      if (b.brick === 'action-tiles') for (const t of (b.props?.tiles as string[] | undefined) ?? []) zones.add(`tile:${t.split('|')[0]}`);
      const slots = b.props?.slots;
      if (Array.isArray(slots)) for (const child of slots as BrickInstance[]) visit(child, seat);
    };
    tab.bricks.forEach(b => visit(b));
    out.set(tab.key, zones);
  }
  return out;
}

/** The tab that draws an anchor (`obj:`, `zone:`, `pile:`, `slot:`), or null when no tab does. */
export function tabOfAnchor(anchor: string, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects'>): string | null {
  const colon = anchor.indexOf(':');
  const type = anchor.slice(0, colon), rest = anchor.slice(colon + 1);
  let zone: string | null = null;
  if (type === 'obj') {
    const o = table.objects.get(rest);
    if (!o) return null;
    zone = o.zoneId;
    // An occupant draws on its host (a worker on a location), so it lives where the host does.
    const host = o.stack ?? o.at;
    if (host && table.objects.has(host) && ![...zonesByTab.values()].some(z => z.has(o.zoneId))) zone = table.objects.get(host)!.zoneId;
  } else if (type === 'zone' || type === 'pile') zone = rest;
  else if (type === 'slot') zone = rest.slice(0, rest.lastIndexOf('#'));
  else if (type === 'tile' || type === 'mat') zone = anchor;
  if (!zone) return null;
  for (const [key, zones] of zonesByTab) if (zones.has(zone)) return key;
  return null;
}

/** Lit anchors per tab key (badge counts). */
export function countByTab(anchors: Iterable<string>, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects'>): Map<string, number> {
  const out = new Map<string, number>();
  for (const a of anchors) {
    const tab = tabOfAnchor(a, zonesByTab, table);
    if (tab) out.set(tab, (out.get(tab) ?? 0) + 1);
  }
  return out;
}

/** Lit choices per tab key: each choice counts once in a tab however many of its anchors that tab draws (`views.badges: "choices"`). */
export function countChoicesByTab(targets: ReadonlyMap<string, readonly { choiceId: string }[]>, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects'>): Map<string, number> {
  const seen = new Map<string, Set<string>>();
  for (const [anchor, entries] of targets) {
    const tab = tabOfAnchor(anchor, zonesByTab, table);
    if (!tab) continue;
    const ids = seen.get(tab) ?? new Set<string>(); seen.set(tab, ids);
    for (const entry of entries) ids.add(entry.choiceId);
  }
  return new Map([...seen].map(([tab, ids]) => [tab, ids.size]));
}

/** Filter navigation/panels only. Keep the full expanded tabs for placement and coverage. */
export function eligibleTabs(tabs: Tab[], zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects' | 'zones'>, anchors: Iterable<string>): Tab[] {
  const lit = [...anchors];
  const counts = countByTab(lit, zonesByTab, table);
  const observedZones = new Set(table.zones.map(zone => zone.id));
  return tabs.filter(tab => {
    if (tab.onlyWithTargets) return (counts.get(tab.key) ?? 0) > 0;
    if (!tab.onlyWithZones) return true;
    const selected = zonesByTab.get(tab.key) ?? new Set<string>();
    // tabZones also contains synthetic mat/tile anchors. Existence means an
    // actual observed zone ID, not merely a nonempty selection or object count.
    if ([...selected].some(id => observedZones.has(id))) return true;
    // Even overlapping tab selectors retain each tab containing a lit target.
    const ownZones = new Map([[tab.key, selected]]);
    return lit.some(anchor => tabOfAnchor(anchor, ownZones, table) !== null);
  });
}

/** Retain the selected eligible leaf, else its group, else the first eligible tab. */
export function shownTab(tabs: Tab[], current: string | null, previousGroup: string | null = null): string | null {
  return tabs.some(tab => tab.key === current) ? current : tabs.find(tab => tab.id === previousGroup)?.key ?? tabs[0]?.key ?? null;
}

/**
 * `single-view-targets`: the tab to show when every lit anchor that lives in a tab lives in the
 * same one. Anchors outside the views (a hand on the mat) do not stop it. Null leaves the tab alone.
 * `passive` tabs (lists that mirror the targets of a board) neither trigger nor block the switch, and a
 * shown tab that already holds a lit anchor stays when passive tabs exist.
 */
export function focusTab(anchors: Iterable<string>, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects'>, passive: ReadonlySet<string> = new Set(), current: string | null = null): string | null {
  const counts = countByTab(anchors, zonesByTab, table);
  if (passive.size > 0 && current !== null && (counts.get(current) ?? 0) > 0) return null;
  const active = [...counts.keys()].filter(key => !passive.has(key));
  return active.length === 1 ? active[0]! : null;
}

/** The tab holding the most of these objects (replay focus), or null. Ties go to the earlier tab. */
export function busiestTab(objectIds: Iterable<string>, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'objects'>): string | null {
  const counts = countByTab([...objectIds].map(id => `obj:${id}`), zonesByTab, table);
  let best: string | null = null, n = 0;
  for (const key of zonesByTab.keys()) { const c = counts.get(key) ?? 0; if (c > n) { best = key; n = c; } }
  return best;
}

/** The tab's own objects (muted badge count when it holds no targets). */
export function objectCount(tabKey: string, zonesByTab: Map<string, Set<string>>, table: Pick<TableModel, 'zones'>): number {
  const zones = zonesByTab.get(tabKey);
  if (!zones) return 0;
  return table.zones.filter(z => zones.has(z.id)).reduce((n, z) => n + z.objects.filter(o => !o.stack).length, 0);
}
